import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "./authConfig";
import App from './App';
import ErrorBoundary from './ErrorBoundary';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Get root element
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found. Make sure there is a div with id="root" in your HTML.');
}

const root = ReactDOM.createRoot(rootElement);

// Initialize MSAL with error handling
msalInstance.initialize()
  .then(() => {
    console.log('MSAL initialized successfully');
    
    // Check URL parameters for AADSTS650057 error (Azure AD sometimes returns errors in URL)
    const urlParams = new URLSearchParams(window.location.search);
    const urlError = urlParams.get('error');
    const urlErrorDescription = urlParams.get('error_description');
    
    if (urlError && (urlError === 'invalid_client' || urlErrorDescription?.includes('AADSTS650057'))) {
      console.warn('AADSTS650057 detected in URL parameters. Automatically retrying with basic OIDC scopes...');
      
      // Store flag and clear URL parameters
      try {
        localStorage.setItem('use_basic_oidc_scopes', 'true');
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) {
        console.warn('Failed to store flag or clean URL:', e);
      }
      
      // Automatically retry with basic OIDC scopes
      try {
        msalInstance.loginRedirect({
          scopes: ["openid", "profile", "email"],
        }).catch((retryError) => {
          console.error('Auto-retry with basic OIDC scopes failed:', retryError);
        });
        // Return early - don't continue with normal flow since we're redirecting
        return;
      } catch (retryError) {
        console.error('Failed to initiate auto-retry from URL error:', retryError);
      }
    }
    
    // Handle redirect response if present
    msalInstance.handleRedirectPromise().then((response) => {
      if (response) {
        console.log('Redirect response received:', response);
        // Clear any stored auth errors on successful login
        sessionStorage.removeItem('auth_error');
        // Clear the basic OIDC scopes flag if login was successful with full scopes
        // (This allows users to try full scopes again if Azure AD gets fixed)
        try {
          // Only clear if we successfully got a token (meaning full scopes worked)
          if (response.accessToken) {
            const tokenScopes = response.scopes || [];
            // If token includes the full scope (not just basic OIDC), clear the flag
            if (tokenScopes.some(s => s.includes('f71dfec3-8146-4229-ab15-a89e86dbb8f4'))) {
              localStorage.removeItem('use_basic_oidc_scopes');
              console.log('Cleared use_basic_oidc_scopes flag - full scopes are working');
            }
          }
        } catch (e) {
          console.warn('Failed to check/clear use_basic_oidc_scopes flag:', e);
        }
      }
    }).catch((error: any) => {
      console.error('Error handling redirect:', error);
      // Check for AADSTS650057 - Invalid resource error
      if (error?.errorCode === 'invalid_client' || 
          error?.message?.includes('AADSTS650057') ||
          error?.errorMessage?.includes('AADSTS650057')) {
        console.warn('AADSTS650057 detected: API permissions not configured in Azure AD. Automatically retrying with basic OIDC scopes...');
        
        // Store flag to use basic OIDC scopes on next login attempt
        try {
          localStorage.setItem('use_basic_oidc_scopes', 'true');
          sessionStorage.setItem('auth_error', JSON.stringify({
            code: 'AADSTS650057',
            message: 'Invalid resource. The client has requested access to a resource which is not listed in the requested permissions in the client\'s application registration.',
            timestamp: Date.now()
          }));
        } catch (storageError) {
          console.warn('Failed to store auth error:', storageError);
        }
        
        // Clear any cached tokens/accounts that might be causing issues
        try {
          msalInstance.clearCache();
          console.log('Cleared MSAL cache due to AADSTS650057 error');
        } catch (clearError) {
          console.warn('Failed to clear MSAL cache:', clearError);
        }
        
        // Automatically retry with basic OIDC scopes (works on first attempt)
        try {
          console.log('Automatically retrying login with basic OIDC scopes...');
          msalInstance.loginRedirect({
            scopes: ["openid", "profile", "email"],
          }).catch((retryError) => {
            console.error('Auto-retry with basic OIDC scopes also failed:', retryError);
            // Store error for App component to display
            try {
              sessionStorage.setItem('auth_error', JSON.stringify({
                code: 'AADSTS650057_RETRY_FAILED',
                message: 'Authentication failed even with basic scopes. Please try again or contact your administrator.',
                timestamp: Date.now()
              }));
            } catch (e) {
              console.warn('Failed to store retry error:', e);
            }
          });
        } catch (retryError) {
          console.error('Failed to initiate auto-retry:', retryError);
        }
      } else {
        // Store other auth errors
        try {
          sessionStorage.setItem('auth_error', JSON.stringify({
            code: error?.errorCode || 'unknown',
            message: error?.message || error?.errorMessage || 'Authentication failed',
            timestamp: Date.now()
          }));
        } catch (storageError) {
          console.warn('Failed to store auth error:', storageError);
        }
      }
    });
    
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <MsalProvider instance={msalInstance}>
            <App />
          </MsalProvider>
        </ErrorBoundary>
      </React.StrictMode>
    );
  })
  .catch((error) => {
    console.error('Failed to initialize MSAL:', error);
    // Render app anyway with error message
    root.render(
      <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '600px', margin: '50px auto' }}>
        <h1 style={{ color: '#d32f2f' }}>Application Initialization Error</h1>
        <p>Failed to initialize authentication. Please check the console for details.</p>
        <p style={{ color: 'red', backgroundColor: '#ffebee', padding: '10px', borderRadius: '4px' }}>
          <strong>Error:</strong> {error?.message || 'Unknown error'}
        </p>
        <button 
          onClick={() => window.location.reload()} 
          style={{ 
            marginTop: '20px', 
            padding: '10px 20px', 
            backgroundColor: '#1976d2', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Reload Page
        </button>
      </div>
    );
  });